/**
 * ElecBid — Statement Reconciliation Page
 * Route: /reconciliation
 * Lists all supplier statements (auto-pulled from Gmail or manually uploaded),
 * shows line-item reconciliation results, and lets Dylan add missing items to
 * expenses + auto-push them to Jobber.
 */

import { useState, useEffect, useMemo } from "react";
import { supabase } from "../lib/supabase";
import StatementReconcile from "./StatementReconcile";

const STATUS_TABS = [
  { value: "all",            label: "All" },
  { value: "matched",        label: "✅ Matched" },
  { value: "missing",        label: "❌ Missing" },
  { value: "amount_mismatch",label: "⚠️ Mismatch" },
  { value: "credit",         label: "🔄 Credit" },
];

const fmt = (n) => n != null
  ? `$${Math.abs(parseFloat(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : "—";

// Small ✓ / ✗ / · cell for the cross-system presence columns. `present` is
// true / false (audit known) or null (audit still loading — show a dim dot).
function PresenceCell({ present, tooltip }) {
  const style = { padding: "9px 8px", textAlign: "center", whiteSpace: "nowrap", fontSize: 14, fontWeight: 700 };
  if (present === null || present === undefined) {
    return <td style={{ ...style, color: "#3a4250" }} title={tooltip}>·</td>;
  }
  return (
    <td style={{ ...style, color: present ? "#22c55e" : "#ef4444" }} title={tooltip}>
      {present ? "✓" : "✗"}
    </td>
  );
}

const fmtDate = (d) => d
  ? new Date(d + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "2-digit" })
  : "—";

const fmtFull = (d) => d
  ? new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
  : "—";

export default function ReconciliationPage({ session }) {
  const [reconciliations, setReconciliations] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortCol, setSortCol]       = useState("date");
  const [sortDir, setSortDir]       = useState("asc");
  const [pushingKey, setPushingKey] = useState(null);
  const [pushResults, setPushResults] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  // Cross-system audit (where each item lives across ElecBid / Jobber / QB).
  // Cached per reconciliation so re-clicking a statement doesn't re-fetch.
  const [auditByReco, setAuditByReco]   = useState({});
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError]     = useState(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => { fetchReconciliations(); }, []);

  async function fetchReconciliations() {
    setLoading(true);
    const { data } = await supabase
      .from("expense_reconciliations")
      .select("*")
      .order("created_at", { ascending: false });
    setReconciliations(data || []);
    setLoading(false);
  }

  function handleSelect(rec) {
    setSelected(rec);
    setSearch("");
    setStatusFilter("all");
    setSortCol("date");
    setSortDir("asc");
    setPushResults({});
    setAuditError(null);
    if (!auditByReco[rec.id]) fetchAudit(rec.id);
  }

  async function fetchAudit(recoId) {
    setAuditLoading(true);
    try {
      const res  = await fetch(`/api/audit-statement-reco?reconciliation_id=${recoId}`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Audit failed");
      setAuditByReco(prev => ({ ...prev, [recoId]: data }));
    } catch (e) {
      setAuditError(e.message || String(e));
    } finally {
      setAuditLoading(false);
    }
  }

  // Index audit results by ref_no for fast per-row lookup. Returns null if
  // audit hasn't loaded yet (callers fall back to the row's own status).
  // Declared before `items` because the items sort needs to read client_name
  // through this map.
  const auditByRef = useMemo(() => {
    const audit = selected ? auditByReco[selected.id] : null;
    if (!audit?.items) return null;
    const map = {};
    for (const a of audit.items) {
      const key = a.statement?.ref_no;
      if (key) map[key] = a;
    }
    return map;
  }, [selected, auditByReco]);

  // Filtered + sorted line items for the selected statement
  const items = useMemo(() => {
    if (!selected?.raw_items?.length) return [];
    let list = [...selected.raw_items];

    if (statusFilter !== "all") {
      list = list.filter(i =>
        i.status === statusFilter ||
        (statusFilter === "matched" && i.status === "pushed")
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.ref_no || "").toLowerCase().includes(q) ||
        (i.po_number || "").toLowerCase().includes(q) ||
        (auditByRef?.[i.ref_no]?.client_name || "").toLowerCase().includes(q) ||
        String(Math.abs(i.amount || 0)).includes(q)
      );
    }

    list.sort((a, b) => {
      let av, bv;
      if      (sortCol === "date")   { av = a.date    || ""; bv = b.date    || ""; }
      else if (sortCol === "ref")    { av = a.ref_no  || ""; bv = b.ref_no  || ""; }
      else if (sortCol === "po")     { av = a.po_number || ""; bv = b.po_number || ""; }
      else if (sortCol === "client") {
        av = (auditByRef?.[a.ref_no]?.client_name || "").toLowerCase();
        bv = (auditByRef?.[b.ref_no]?.client_name || "").toLowerCase();
      }
      else if (sortCol === "amt")    { av = parseFloat(a.amount) || 0; bv = parseFloat(b.amount) || 0; }
      else if (sortCol === "status") { av = a.status  || ""; bv = b.status  || ""; }
      else return 0;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ?  1 : -1;
      return 0;
    });

    return list;
  }, [selected, statusFilter, search, sortCol, sortDir, auditByRef]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sortArrow = (col) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  // Stable key for matching a line item back into raw_items after a push.
  const itemKey = (i) => i.ref_no || (String(i.amount) + (i.date || ""));

  function getAudit(item) {
    return auditByRef?.[item.ref_no] || null;
  }

  // Decide what action button (if any) to show for a row, given its audit
  // verdict. Returns null when no manual action is appropriate (already in
  // every system, backfill-leftover, awaiting nightly QB push, etc.).
  function actionFor(item, a) {
    // Ignored items short-circuit all action logic — the only thing
    // available is "Undo ignore", rendered separately in the action cell.
    if (item.ignored || item.status === "ignored") return null;

    if (!a) {
      // Audit not loaded yet — fall back to the original status-based action
      const isCredit = (item.amount || 0) < 0 || item.status === "credit";
      if (item.status === "missing" || item.status === "amount_mismatch" || item.status === "credit") {
        return {
          kind:  "addAndPush",
          label: isCredit
            ? (item.po_number ? "Push Credit →" : "Add Credit")
            : (item.po_number ? "Add + Push →" : "Add to Expenses"),
          color: isCredit ? "#a78bfa" : "#f59e0b",
        };
      }
      return null;
    }

    const isCredit = (a.statement.amount || 0) < 0;
    switch (a.verdict) {
      case "fully_logged":
      case "elecbid_jobber_only_missing_qb":
        return null;
      case "elecbid_qb_only_missing_jobber":
      case "elecbid_only_not_pushed":
        if (!a.elecbid?.id) return null;
        if (!a.elecbid?.po_number || !/\d+/.test(String(a.elecbid.po_number))) {
          return { kind: "noop", label: "No numeric PO", color: "#6b7280", disabled: true };
        }
        return {
          kind:  "pushToJobber",
          piId:  a.elecbid.id,
          label: isCredit ? "Push Credit to Jobber" : "Push to Jobber",
          color: isCredit ? "#a78bfa" : "#3b82f6",
        };
      case "qb_only_bypassed_elecbid":
        // Item exists in QB but never made it into ElecBid/Jobber. Adding it
        // to ElecBid would normally trigger another QB push tomorrow night —
        // we pass the audit's QB bill_id through to addAndPush so the backend
        // pre-stamps the new PI as already-published-to-QB, no double Bill.
        if (!a.qb?.bill?.id) return null;  // defensive — no QB id to link
        return {
          kind:  "addAndPush",
          label: isCredit
            ? (a.statement.po_number ? "Add Credit (EB+JB)" : "Add Credit")
            : (a.statement.po_number ? "Add EB + Jobber" : "Add to Expenses"),
          color: isCredit ? "#a78bfa" : "#f59e0b",
        };
      case "weak_qb_candidate":
        return { kind: "review", label: "Check QB manually", color: "#f59e0b", disabled: true };
      case "actually_missing":
        return {
          kind:  "addAndPush",
          label: isCredit
            ? (a.statement.po_number ? "Push Credit →" : "Add Credit")
            : (a.statement.po_number ? "Add + Push →" : "Add to Expenses"),
          color: isCredit ? "#a78bfa" : "#f59e0b",
        };
      default:
        return null;
    }
  }

  // Optimistically mark an audit item as pushed-to-Jobber after a successful
  // push, so the UI flips to ✓✓✓ without a re-fetch. Also updates raw_items
  // status (mirrors the existing addAndPush flow).
  function applyJobberPushed(refNo, expenseId, jobNumber) {
    setAuditByReco(prev => {
      if (!selected || !prev[selected.id]) return prev;
      const old = prev[selected.id];
      const newItems = (old.items || []).map(a => {
        if (a.statement?.ref_no !== refNo) return a;
        const stillNeedsQb = !a.qb.present;
        return {
          ...a,
          jobber:  { ...a.jobber, present: true, expense_id: expenseId, job_number: jobNumber },
          verdict: stillNeedsQb ? "elecbid_jobber_only_missing_qb" : "fully_logged",
        };
      });
      return { ...prev, [selected.id]: { ...old, items: newItems } };
    });
  }

  // Recompute the summary counts on a reconciliation row given fresh raw_items.
  // Mirrors the count logic in api/reconcile-statement.js handleSave().
  function recomputeCounts(items) {
    const r = { matched_count: 0, mismatch_count: 0, missing_count: 0, credit_count: 0 };
    for (const i of items || []) {
      if (i.status === "matched" || i.status === "pushed")  r.matched_count++;
      else if (i.status === "amount_mismatch")              r.mismatch_count++;
      else if (i.status === "missing")                      r.missing_count++;
      else if (i.status === "credit")                       r.credit_count++;
    }
    return r;
  }

  async function handleAddAndPush(item) {
    const key = itemKey(item);
    setPushingKey(key);
    try {
      // If the audit has confirmed this item is already in QB, forward the
      // matched QB bill so the backend can link the new PI to it instead of
      // letting the nightly QB push create a duplicate Bill.
      const a = getAudit(item);
      const body = { action: "addAndPush", item, supplier: selected?.supplier };
      if (a?.qb?.present && a?.qb?.bill?.id) {
        body.qb_already_in = {
          bill_id:    a.qb.bill.id,
          type:       a.qb.bill.type,
          doc_number: a.qb.bill.doc_number,
          total:      a.qb.bill.total,
        };
      }
      const res = await fetch("/api/reconcile-statement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      setPushResults(prev => ({ ...prev, [key]: data }));

      // On any successful response — including "duplicate" — the line item is
      // no longer in a missing/credit-needing-action state. Flip its status,
      // recompute the summary counts, and persist both back to Supabase so a
      // page refresh doesn't revert the reconciliation row to its old state.
      if (data.ok) {
        const newStatus = data.result === "pushed" ? "pushed" : "matched";
        const newItems = (selected?.raw_items || []).map(i =>
          itemKey(i) === key ? { ...i, status: newStatus } : i
        );
        const newCounts = recomputeCounts(newItems);

        setSelected(prev => prev ? { ...prev, raw_items: newItems, ...newCounts } : prev);
        setReconciliations(prev => prev.map(r =>
          r.id === selected?.id ? { ...r, raw_items: newItems, ...newCounts } : r
        ));

        // Optimistically reflect the push in the audit cache so the
        // ElecBid/Jobber/QB columns update without a re-fetch.
        const pushedToJobber = data.result === "pushed";
        setAuditByReco(prev => {
          if (!selected || !prev[selected.id]) return prev;
          const old = prev[selected.id];
          const newAItems = (old.items || []).map(a => {
            if (a.statement?.ref_no !== item.ref_no) return a;
            const updated = {
              ...a,
              elecbid: a.elecbid || { id: data.id, status: pushedToJobber ? "pushed" : "pending", supplier: selected.supplier, po_number: item.po_number, total: item.amount },
              jobber:  pushedToJobber
                ? { ...a.jobber, present: true, expense_id: data.expenseId, job_number: data.jobNumber }
                : a.jobber,
            };
            const inEb = !!updated.elecbid;
            const inJb = updated.jobber.present;
            const inQb = updated.qb.present;
            updated.verdict = inEb && inJb && inQb
              ? "fully_logged"
              : inEb && inQb && !inJb
                ? "elecbid_qb_only_missing_jobber"
                : inEb && inJb && !inQb
                  ? "elecbid_jobber_only_missing_qb"
                  : "elecbid_only_not_pushed";
            return updated;
          });
          return { ...prev, [selected.id]: { ...old, items: newAItems } };
        });

        if (selected?.id) {
          // Persist. Failure here is non-fatal — local state still updated.
          const { error } = await supabase
            .from("expense_reconciliations")
            .update({ raw_items: newItems, ...newCounts })
            .eq("id", selected.id);
          if (error) console.warn("[reconciliation] persist failed:", error.message);
        }
      }
    } catch (e) {
      setPushResults(prev => ({ ...prev, [key]: { ok: false, error: e.message } }));
    } finally {
      setPushingKey(null);
    }
  }

  // Push an existing pending_invoices row (already in ElecBid) onto the
  // Jobber timeline. Used when the audit shows in-ElecBid + in-QB but no
  // Jobber expense — typically credits we now want pushed, or rows where
  // the original Jobber push failed and was never retried.
  async function handlePushToJobber(item, piId) {
    const key = itemKey(item);
    setPushingKey(key);
    try {
      // Forward the statement's signed amount as the source of truth — the PI
      // row may have a stale-positive total if the original email parser
      // didn't recognize a refund-style invoice as a credit. The backend uses
      // this to (a) push the right sign to Jobber and (b) repair the PI row.
      const stmtAmount = (typeof item.amount === "number") ? item.amount : null;
      const body = { action: "pushPendingToJobber", pending_invoice_id: piId };
      if (stmtAmount !== null) body.signed_amount = stmtAmount;
      const res = await fetch("/api/reconcile-statement", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      setPushResults(prev => ({ ...prev, [key]: data }));

      if (data.ok && data.result === "pushed") {
        applyJobberPushed(item.ref_no, data.expenseId, data.jobNumber);
        // Bump the line's raw_items status to "pushed" so the reco summary
        // pills update (mirrors the addAndPush flow). Persist back too.
        const newItems = (selected?.raw_items || []).map(i =>
          itemKey(i) === key ? { ...i, status: "pushed" } : i
        );
        const newCounts = recomputeCounts(newItems);
        setSelected(prev => prev ? { ...prev, raw_items: newItems, ...newCounts } : prev);
        setReconciliations(prev => prev.map(r =>
          r.id === selected?.id ? { ...r, raw_items: newItems, ...newCounts } : r
        ));
        if (selected?.id) {
          const { error } = await supabase
            .from("expense_reconciliations")
            .update({ raw_items: newItems, ...newCounts })
            .eq("id", selected.id);
          if (error) console.warn("[reconciliation] persist failed:", error.message);
        }
      }
    } catch (e) {
      setPushResults(prev => ({ ...prev, [key]: { ok: false, error: e.message } }));
    } finally {
      setPushingKey(null);
    }
  }

  // Park a statement line as "ignored" with a note. Used for cases like a
  // credit on the statement whose original positive expense never made it
  // into Jobber — pushing the credit would falsely inflate job profit, so
  // we'd rather mark the line resolved and leave a paper trail of why.
  async function handleIgnore(item) {
    const note = window.prompt(
      `Why are you ignoring this line? (e.g. "credit for an invoice that was never recorded as an expense in Jobber, so pushing it would falsely inflate job profit")`,
      ""
    );
    if (note === null) return;  // user cancelled
    const trimmed = note.trim();
    if (!trimmed) {
      window.alert("Note is required — try again.");
      return;
    }

    const key = itemKey(item);
    setPushingKey(key);
    try {
      const res = await fetch("/api/reconcile-statement", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          action: "ignoreItem",
          reconciliation_id: selected?.id,
          ref_no: item.ref_no || null,
          amount: item.amount,
          note:   trimmed,
          ignored_by: session?.user?.email || null,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to ignore");

      // Mirror the saved state into raw_items so the UI flips to ignored
      // immediately. Counts come back from the server so they match.
      const newItems = (selected?.raw_items || []).map(i =>
        itemKey(i) === key
          ? { ...i, ignored: true, ignore_note: trimmed, ignored_at: data.item?.ignored_at, status_before_ignore: i.status_before_ignore || i.status, status: "ignored" }
          : i
      );
      setSelected(prev => prev ? { ...prev, raw_items: newItems, ...(data.counts || {}) } : prev);
      setReconciliations(prev => prev.map(r =>
        r.id === selected?.id ? { ...r, raw_items: newItems, ...(data.counts || {}) } : r
      ));
    } catch (e) {
      setPushResults(prev => ({ ...prev, [key]: { ok: false, error: e.message } }));
    } finally {
      setPushingKey(null);
    }
  }

  async function handleUnignore(item) {
    const key = itemKey(item);
    setPushingKey(key);
    try {
      const res = await fetch("/api/reconcile-statement", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          action: "unignoreItem",
          reconciliation_id: selected?.id,
          ref_no: item.ref_no || null,
          amount: item.amount,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to unignore");

      const newItems = (selected?.raw_items || []).map(i =>
        itemKey(i) === key
          ? { ...i, ignored: false, ignore_note: null, ignored_at: null, status: data.item?.status || i.status_before_ignore || "missing", status_before_ignore: null }
          : i
      );
      setSelected(prev => prev ? { ...prev, raw_items: newItems, ...(data.counts || {}) } : prev);
      setReconciliations(prev => prev.map(r =>
        r.id === selected?.id ? { ...r, raw_items: newItems, ...(data.counts || {}) } : r
      ));
    } catch (e) {
      setPushResults(prev => ({ ...prev, [key]: { ok: false, error: e.message } }));
    } finally {
      setPushingKey(null);
    }
  }

  // Summary counts for selected statement (from live raw_items so optimistic updates show)
  const summary = useMemo(() => {
    if (!selected?.raw_items) return {};
    return selected.raw_items.reduce((acc, i) => {
      acc[i.status] = (acc[i.status] || 0) + 1;
      return acc;
    }, {});
  }, [selected]);

  const matchedCount  = (summary.matched || 0) + (summary.pushed || 0);
  const missingCount  = summary.missing || 0;
  const mismatchCount = summary.amount_mismatch || 0;

  return (
    <div style={{ background: "#0d0f12", minHeight: "100vh", padding: "0 0 80px 0", color: "#f0f0f0" }}>

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>📄 Statement Reconciliation</h1>
            <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
              Supplier statements auto-pulled from Gmail + manually uploaded. Click any statement to see line-item results.
            </p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", color: "#f59e0b", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            + Upload Statement
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 20px", display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* ── Statement list ───────────────────────────────────────────────────── */}
        <div style={{ width: selected ? 292 : "100%", flexShrink: 0, display: isMobile && selected ? "none" : undefined }}>
          {loading ? (
            <div style={{ color: "#6b7280", padding: "32px 0", textAlign: "center", fontSize: 14 }}>Loading statements…</div>
          ) : reconciliations.length === 0 ? (
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 12, padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No statements yet</div>
              <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 20 }}>
                Statements appear here automatically when Gmail pulls them, or you can upload one manually.
              </div>
              <button onClick={() => setShowUpload(true)} style={{ background: "#f59e0b", color: "#0d0f12", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                Upload Statement
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {reconciliations.map(rec => {
                const isSelected = selected?.id === rec.id;
                const autoGmail  = rec.created_by === "gmail-auto";
                const recMatched = (rec.matched_count || 0);
                const recMissing = (rec.missing_count || 0);
                const recMismatch = (rec.mismatch_count || 0);

                return (
                  <button
                    key={rec.id}
                    onClick={() => handleSelect(rec)}
                    style={{
                      background: isSelected ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.03)",
                      border: `1px solid ${isSelected ? "rgba(245,158,11,0.45)" : "rgba(255,255,255,0.08)"}`,
                      borderRadius: 10,
                      padding: "12px 14px",
                      textAlign: "left",
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 3 }}>
                      <span style={{ color: "#f0f0f0", fontWeight: 600, fontSize: 14 }}>
                        {rec.supplier || "Unknown supplier"}
                      </span>
                      {autoGmail && (
                        <span style={{ fontSize: 11, color: "#60a5fa", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)", borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap", flexShrink: 0 }}>
                          📧 Gmail
                        </span>
                      )}
                    </div>
                    <div style={{ color: "#8a919c", fontSize: 12, marginBottom: 7 }}>
                      {rec.statement_date ? fmtDate(rec.statement_date) : "No date"}
                      &nbsp;·&nbsp;
                      {fmt(rec.total_on_statement)}
                      &nbsp;·&nbsp;
                      <span style={{ color: "#5a6270" }}>{fmtFull(rec.created_at)}</span>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {recMatched > 0  && <span style={{ fontSize: 11, color: "#22c55e" }}>✅ {recMatched}</span>}
                      {recMissing > 0  && <span style={{ fontSize: 11, color: "#ef4444" }}>❌ {recMissing} missing</span>}
                      {recMismatch > 0 && <span style={{ fontSize: 11, color: "#f59e0b" }}>⚠️ {recMismatch}</span>}
                      {(rec.credit_count || 0) > 0 && <span style={{ fontSize: 11, color: "#a78bfa" }}>🔄 {rec.credit_count}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Detail panel ─────────────────────────────────────────────────────── */}
        {selected && (
          <div style={{ flex: 1, minWidth: 0, width: isMobile ? "100%" : undefined }}>

            {/* Mobile back button */}
            {isMobile && (
              <button
                onClick={() => setSelected(null)}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#f59e0b", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "0 0 12px 0" }}
              >
                ← Back to statements
              </button>
            )}

            {/* Detail header card */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "14px 18px", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17 }}>
                    {selected.supplier || "Unknown supplier"}
                    {selected.statement_date && (
                      <span style={{ color: "#8a919c", fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                        · {fmtDate(selected.statement_date)}
                      </span>
                    )}
                    {selected.created_by === "gmail-auto" && (
                      <span style={{ fontSize: 12, color: "#60a5fa", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)", borderRadius: 4, padding: "2px 7px", marginLeft: 8, fontWeight: 500 }}>
                        📧 Auto from Gmail
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 20, marginTop: 6, flexWrap: "wrap" }}>
                    <span style={{ color: "#8a919c", fontSize: 13 }}>
                      Statement: <strong style={{ color: "#f0f0f0" }}>{fmt(selected.total_on_statement)}</strong>
                    </span>
                    <span style={{ color: "#8a919c", fontSize: 13 }}>
                      In ElecBid: <strong style={{ color: "#f0f0f0" }}>{fmt(selected.total_in_elecbid)}</strong>
                    </span>
                    {(() => {
                      const variance = (selected.total_on_statement || 0) - (selected.total_in_elecbid || 0);
                      const absVar = Math.abs(variance);
                      return (
                        <span style={{ color: "#8a919c", fontSize: 13 }}>
                          Variance: <strong style={{ color: absVar < 0.02 ? "#22c55e" : "#f59e0b" }}>{fmt(variance)}</strong>
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  style={{ background: "transparent", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 2 }}
                  title="Close detail"
                >✕</button>
              </div>

              {/* Status summary pills */}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {matchedCount > 0  && <span style={{ background: "rgba(34,197,94,0.08)",   border: "1px solid rgba(34,197,94,0.2)",   borderRadius: 6, padding: "3px 9px", fontSize: 12, color: "#22c55e" }}>✅ {matchedCount} matched</span>}
                {missingCount > 0  && <span style={{ background: "rgba(239,68,68,0.08)",   border: "1px solid rgba(239,68,68,0.2)",   borderRadius: 6, padding: "3px 9px", fontSize: 12, color: "#ef4444" }}>❌ {missingCount} missing</span>}
                {mismatchCount > 0 && <span style={{ background: "rgba(245,158,11,0.08)",  border: "1px solid rgba(245,158,11,0.2)",  borderRadius: 6, padding: "3px 9px", fontSize: 12, color: "#f59e0b" }}>⚠️ {mismatchCount} mismatch</span>}
                {(summary.credit || 0) > 0 && <span style={{ background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: 6, padding: "3px 9px", fontSize: 12, color: "#a78bfa" }}>🔄 {summary.credit} credit</span>}
              </div>
            </div>

            {/* Filter bar */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search invoice #, PO, amount…"
                style={{ flex: 1, minWidth: 160, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "7px 12px", color: "#f0f0f0", fontSize: 13, outline: "none" }}
              />
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {STATUS_TABS.map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => setStatusFilter(tab.value)}
                    style={{
                      background: statusFilter === tab.value ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${statusFilter === tab.value ? "rgba(245,158,11,0.4)" : "rgba(255,255,255,0.1)"}`,
                      color: statusFilter === tab.value ? "#f59e0b" : "#8a919c",
                      borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", fontWeight: 500,
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Audit status banner */}
            {(auditLoading || auditError) && (
              <div style={{
                marginBottom: 8,
                padding: "6px 12px",
                fontSize: 12,
                borderRadius: 8,
                background:    auditError ? "rgba(239,68,68,0.06)" : "rgba(96,165,250,0.06)",
                border: `1px solid ${auditError ? "rgba(239,68,68,0.2)" : "rgba(96,165,250,0.2)"}`,
                color:         auditError ? "#ef4444" : "#60a5fa",
              }}>
                {auditError
                  ? `Cross-system audit failed: ${auditError} — falling back to single-status view`
                  : "Loading cross-system audit (ElecBid / Jobber / QB)…"}
              </div>
            )}

            {/* Line items table */}
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      {[
                        { key: "date",   label: "Inv Date"  },
                        { key: "ref",    label: "Invoice #" },
                        { key: "po",     label: "PO / Job"  },
                        { key: "client", label: "Client"    },
                        { key: "amt",    label: "Amount"    },
                      ].map(col => (
                        <th
                          key={col.key}
                          onClick={() => toggleSort(col.key)}
                          style={{ padding: "9px 12px", textAlign: "left", color: "#8a919c", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}
                        >
                          {col.label}{sortArrow(col.key)}
                        </th>
                      ))}
                      <th style={{ padding: "9px 8px", color: "#8a919c", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" }} title="In ElecBid (pending_invoices)">EB</th>
                      <th style={{ padding: "9px 8px", color: "#8a919c", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" }} title="In Jobber (expense on a job)">JB</th>
                      <th style={{ padding: "9px 8px", color: "#8a919c", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" }} title="In QuickBooks (Bill or VendorCredit)">QB</th>
                      <th style={{ padding: "9px 12px", color: "#8a919c", fontWeight: 600, textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: "28px", textAlign: "center", color: "#6b7280" }}>
                          No items match your filters
                        </td>
                      </tr>
                    ) : items.map((item, idx) => {
                      const key         = itemKey(item);
                      const pushResult  = pushResults[key];
                      const isPushing   = pushingKey === key;
                      const a           = getAudit(item);
                      const isCredit    = (item.amount || 0) < 0 || item.status === "credit";
                      const wasAdded    = pushResult?.ok && pushResult?.result !== "duplicate" && pushResult?.result !== "already_in_jobber";
                      const isDuplicate = pushResult?.result === "duplicate" || pushResult?.result === "already_in_jobber";
                      const action      = actionFor(item, a);

                      // Presence cells: tri-state ✓ / ✗ / ? (audit not loaded)
                      const inEb = a ? !!a.elecbid : null;
                      const inJb = a ? a.jobber.present : null;
                      const inQb = a ? a.qb.present     : null;

                      return (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            background: wasAdded ? "rgba(34,197,94,0.03)" : "transparent",
                          }}
                        >
                          {/* Date */}
                          <td style={{ padding: "9px 12px", color: "#c0c4cc", whiteSpace: "nowrap" }}>
                            {fmtDate(item.date)}
                          </td>

                          {/* Invoice # */}
                          <td style={{ padding: "9px 12px", color: "#f0f0f0", fontFamily: "monospace", fontSize: 12 }}>
                            {item.ref_no || <span style={{ color: "#4b5563" }}>—</span>}
                          </td>

                          {/* PO / Job */}
                          <td style={{ padding: "9px 12px" }}>
                            {item.po_number ? (
                              <span style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 5, padding: "2px 7px", color: "#f59e0b", fontSize: 12 }}>
                                #{item.po_number}
                              </span>
                            ) : <span style={{ color: "#4b5563" }}>—</span>}
                          </td>

                          {/* Client */}
                          <td style={{ padding: "9px 12px", color: "#c0c4cc", fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a?.client_name || ""}>
                            {a?.client_name
                              ? a.client_name
                              : a
                                ? <span style={{ color: "#4b5563" }}>—</span>
                                : <span style={{ color: "#3a4250" }}>·</span>}
                          </td>

                          {/* Amount */}
                          <td style={{ padding: "9px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>
                            <span style={{ color: (item.amount || 0) < 0 ? "#a78bfa" : "#f0f0f0" }}>
                              {(item.amount || 0) < 0 ? "-" : ""}{fmt(item.amount)}
                            </span>
                            {a?.statement.transaction_type === "CREDIT" && (
                              <span style={{ color: "#a78bfa", fontSize: 10, marginLeft: 4, fontWeight: 500 }}>credit</span>
                            )}
                          </td>

                          {/* In ElecBid */}
                          <PresenceCell
                            present={inEb}
                            tooltip={a?.elecbid ? `PI #${String(a.elecbid.id).slice(0,8)} · status=${a.elecbid.status}${a.elecbid.flag_reason ? ` · ${a.elecbid.flag_reason}` : ""}` : (a ? "Not in pending_invoices" : "Loading…")}
                          />

                          {/* In Jobber */}
                          <PresenceCell
                            present={inJb}
                            tooltip={a?.jobber.present ? `Jobber expense on Job #${a.jobber.job_number}` : (a ? "No Jobber expense" : "Loading…")}
                          />

                          {/* In QB */}
                          <PresenceCell
                            present={inQb}
                            tooltip={(() => {
                              if (!a)               return "Loading…";
                              if (!a.qb.present)    return `Not in QB (${a.qb.match_reason || "no candidate"})`;
                              const b = a.qb.bill;
                              const provenance = [b?.description, b?.memo, b?.private_note]
                                .filter(Boolean)
                                .find(t => /\[ElecBid\]/i.test(t));
                              return [
                                `${b?.type || "QB"} ${b?.doc_number || ""}`,
                                a.qb.match_reason,
                                provenance ? "Pushed by ElecBid (description marker)" : null,
                              ].filter(Boolean).join(" · ");
                            })()}
                          />

                          {/* Action */}
                          <td style={{ padding: "9px 12px", textAlign: "right" }}>
                            {item.ignored ? (
                              <span
                                title={item.ignore_note ? `Ignored: ${item.ignore_note}` : "Ignored"}
                                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                              >
                                <span style={{ color: "#9ca3af", fontSize: 11, fontStyle: "italic" }}>
                                  🚫 Ignored{item.ignore_note ? ` — ${item.ignore_note.length > 40 ? item.ignore_note.slice(0, 40) + "…" : item.ignore_note}` : ""}
                                </span>
                                <button
                                  onClick={() => handleUnignore(item)}
                                  disabled={isPushing}
                                  title="Undo ignore — restore the previous status"
                                  style={{ background: "none", border: "none", color: "#6b7280", cursor: isPushing ? "default" : "pointer", fontSize: 11, padding: "2px 4px", textDecoration: "underline" }}
                                >
                                  undo
                                </button>
                              </span>
                            ) : wasAdded ? (
                              <span style={{ color: "#22c55e", fontSize: 12 }}>
                                ✅ {pushResult.result === "pushed"
                                  ? `${isCredit ? "Credit pushed" : "Pushed"} → Job #${pushResult.jobNumber}`
                                  : pushResult.result === "flagged_no_job"
                                    ? `${isCredit ? "Credit added" : "Added"} (no job match)`
                                  : pushResult.result === "flagged_job_closed"
                                    ? `${isCredit ? "Credit added" : "Added"} (job closed)`
                                  : pushResult.result === "flagged_jobber_error"
                                    ? "Added · Jobber failed (will retry)"
                                  : pushResult.result === "added_no_po"
                                    ? `${isCredit ? "Credit added" : "Added"} (no PO)`
                                    : `${isCredit ? "Credit" : "Item"} added`}
                              </span>
                            ) : isDuplicate ? (
                              <span style={{ color: "#6b7280", fontSize: 12 }}>
                                {pushResult.result === "already_in_jobber" ? "Already in Jobber" : "Already in expenses"}
                              </span>
                            ) : action ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <button
                                  onClick={() => {
                                    if (action.disabled) return;
                                    if (action.kind === "addAndPush")    handleAddAndPush(item);
                                    else if (action.kind === "pushToJobber") handlePushToJobber(item, action.piId);
                                  }}
                                  disabled={isPushing || action.disabled}
                                  title={action.kind === "review" ? "Audit found a weak match — verify in QB by hand. If truly missing, you can mark it manually." : undefined}
                                  style={{
                                    background: action.disabled ? "rgba(255,255,255,0.04)" : `${action.color}22`,
                                    border:     `1px solid ${action.disabled ? "rgba(255,255,255,0.1)" : `${action.color}66`}`,
                                    color:      action.disabled ? "#6b7280" : action.color,
                                    borderRadius: 6,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    cursor: (isPushing || action.disabled) ? "default" : "pointer",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    opacity: isPushing ? 0.6 : 1,
                                  }}
                                >
                                  {isPushing ? (action.kind === "pushToJobber" ? "Pushing…" : "Adding…") : action.label}
                                </button>
                                {!action.disabled && (
                                  <button
                                    onClick={() => handleIgnore(item)}
                                    disabled={isPushing}
                                    title="Skip this line and leave a note explaining why (e.g. credit for an invoice that was never recorded in Jobber)"
                                    style={{ background: "none", border: "none", color: "#6b7280", cursor: isPushing ? "default" : "pointer", fontSize: 11, padding: "2px 4px", textDecoration: "underline" }}
                                  >
                                    ignore
                                  </button>
                                )}
                              </span>
                            ) : a?.verdict === "qb_only_bypassed_elecbid" ? (
                              <span style={{ color: "#6b7280", fontSize: 11, fontStyle: "italic" }} title="In QB only — likely a backfill leftover. Already covered, no push needed.">
                                In QB only
                              </span>
                            ) : a?.verdict === "elecbid_jobber_only_missing_qb" ? (
                              <span style={{ color: "#6b7280", fontSize: 11, fontStyle: "italic" }} title="Awaits the nightly QB push.">
                                QB pending
                              </span>
                            ) : null}
                            {pushResult && !pushResult.ok && (
                              <span style={{ color: "#ef4444", fontSize: 11, marginLeft: 6 }} title={pushResult.error}>
                                ✕ {pushResult.error?.slice(0, 40) || "Error"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Table footer */}
              {items.length > 0 && (
                <div style={{ padding: "8px 14px", borderTop: "1px solid rgba(255,255,255,0.06)", color: "#6b7280", fontSize: 12, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <span>
                    {items.length} item{items.length !== 1 ? "s" : ""}
                    {(statusFilter !== "all" || search.trim()) ? ` (filtered from ${selected.raw_items?.length || 0} total)` : ""}
                  </span>
                  {!search && statusFilter === "all" && (missingCount > 0 || (summary.credit || 0) > 0) && (
                    <span style={{ display: "inline-flex", gap: 12, flexWrap: "wrap" }}>
                      {missingCount > 0 && (
                        <span style={{ color: "#ef4444" }}>
                          {missingCount} missing — "Add + Push" to send to expenses
                        </span>
                      )}
                      {(summary.credit || 0) > 0 && (
                        <span style={{ color: "#a78bfa" }}>
                          {summary.credit} credit{summary.credit !== 1 ? "s" : ""} — "Push Credit" to record in QB
                        </span>
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manual upload modal */}
      {showUpload && (
        <StatementReconcile
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); fetchReconciliations(); }}
          session={session}
        />
      )}
    </div>
  );
}
